import { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCog, Plus, Search, Mail, Trash2, Shield, Users, Link2, Copy, CheckCircle, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const ClubCoaches = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [coaches, setCoaches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    team_id: '',
    role: 'assistant'
  });
  const [editForm, setEditForm] = useState({
    team_id: '',
    role: '',
    status: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const [coachesRes, teamsRes] = await Promise.all([
        axios.get(`${API}/clubs/coaches`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clubs/teams`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Transform coaches data from club-wide format to team-assignment format
      // Each coach can be in multiple teams, so we flatten the data
      const transformedCoaches = [];
      (coachesRes.data || []).forEach(coach => {
        (coach.teams || []).forEach(teamInfo => {
          transformedCoaches.push({
            id: `${coach.id}_${teamInfo.team_id}`,
            coach_id: coach.coach_id || coach.id,
            coach_name: coach.full_name,
            coach_email: coach.email,
            status: coach.status,
            team_id: teamInfo.team_id,
            team_name: teamInfo.team_name,
            role: teamInfo.role,
            assigned_at: teamInfo.assigned_at
          });
        });
      });
      
      setCoaches(transformedCoaches);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const generateInviteLink = (teamId, role) => {
    const team = teams.find(t => t.id === teamId);
    const inviteCode = btoa(JSON.stringify({
      clubId,
      teamId,
      role,
      clubName: club?.name,
      teamName: team?.name,
      type: 'coach',
      created: Date.now()
    }));
    return `${APP_URL}/team/join/${inviteCode}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.team_id) {
      toast.error('Please select a team');
      return;
    }
    
    // Generate invite link
    const inviteLink = generateInviteLink(inviteForm.team_id, inviteForm.role);
    
    const token = localStorage.getItem('club_token');
    try {
      await axios.post(`${API}/clubs/teams/${inviteForm.team_id}/coaches`, {
        email: inviteForm.email,
        role: inviteForm.role,
        permissions: ["athletes", "sessions", "drills", "plans", "videos"]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coach invitation sent!');
      setShowInviteModal(false);
      // Show invite link modal
      setGeneratedInviteLink(inviteLink);
      setShowInviteLinkModal(true);
      setInviteForm({ email: '', team_id: '', role: 'assistant' });
      fetchData();
    } catch (error) {
      // Even if email fails, show the invite link
      setGeneratedInviteLink(inviteLink);
      setShowInviteModal(false);
      setShowInviteLinkModal(true);
      toast.info('Email may not have been sent. Use the invite link below.');
    }
  };

  const handleGenerateLinkOnly = () => {
    if (!inviteForm.team_id) {
      toast.error('Please select a team');
      return;
    }
    const inviteLink = generateInviteLink(inviteForm.team_id, inviteForm.role);
    setGeneratedInviteLink(inviteLink);
    setShowInviteModal(false);
    setShowInviteLinkModal(true);
  };

  const handleRemove = async (teamId, coachId) => {
    if (!confirm('Are you sure you want to remove this coach from the team?')) return;
    const token = localStorage.getItem('club_token');
    try {
      await axios.delete(`${API}/clubs/teams/${teamId}/coaches/${coachId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coach removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove coach');
    }
  };

  const handleEdit = (coach) => {
    setSelectedCoach(coach);
    setEditForm({
      team_id: coach.team_id,
      role: coach.role,
      status: coach.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdateCoach = async (e) => {
    e.preventDefault();
    if (!selectedCoach) return;
    
    const token = localStorage.getItem('club_token');
    try {
      await axios.put(`${API}/clubs/coaches/${selectedCoach.coach_id}/assignment`, {
        team_id: editForm.team_id !== selectedCoach.team_id ? editForm.team_id : undefined,
        role: editForm.role !== selectedCoach.role ? editForm.role : undefined,
        status: editForm.status !== selectedCoach.status ? editForm.status : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coach updated successfully');
      setShowEditModal(false);
      setSelectedCoach(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update coach');
    }
  };

  const filteredCoaches = coaches.filter(coach =>
    coach.coach_name?.toLowerCase().includes(search.toLowerCase()) ||
    coach.coach_email?.toLowerCase().includes(search.toLowerCase()) ||
    coach.team_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  return (
    <div className="space-y-6" data-testid="club-coaches">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Coaches <span className="text-primary">Management</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Assign and manage team coaches
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowInviteModal(true)}
            className="bg-primary text-black hover:bg-primary/90"
            disabled={teams.length === 0}
          >
            <Plus size={18} className="mr-2" />
            Invite Coach
          </Button>
        </div>
      </div>

      {teams.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
          <p className="text-sm text-yellow-400">
            Create a team first before inviting coaches.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search coaches..."
          className="bg-transparent border-border pl-10"
        />
      </div>

      {/* Coaches List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredCoaches.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <UserCog className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground mb-4">
            {search ? 'No coaches found' : 'No coaches assigned yet'}
          </p>
          {!search && teams.length > 0 && (
            <Button onClick={() => setShowInviteModal(true)} className="bg-primary text-black hover:bg-primary/90">
              <Plus size={18} className="mr-2" />
              Invite Your First Coach
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-card/50 border-b border-border/40">
              <tr>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Coach</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Team</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Role</th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground p-4">Assigned</th>
                <th className="text-right text-xs uppercase tracking-widest text-muted-foreground p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoaches.map((coach, idx) => (
                <motion.tr
                  key={coach.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="border-b border-border/20 hover:bg-card/50"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        coach.status === 'active' ? 'bg-primary/20' : 'bg-amber-500/20'
                      }`}>
                        <UserCog className={coach.status === 'active' ? 'text-primary' : 'text-amber-400'} size={18} />
                      </div>
                      <div>
                        <p className="font-medium">{coach.coach_name}</p>
                        <p className="text-xs text-muted-foreground">{coach.coach_email}</p>
                        {coach.status === 'pending' && (
                          <span className="text-xs text-amber-400">Pending Registration</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm">{coach.team_name}</span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-sm ${
                      coach.role === 'head_coach' 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {coach.role === 'head_coach' ? 'Head Coach' : 'Assistant'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(coach.assigned_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(coach)}
                        className="text-muted-foreground hover:text-primary"
                        data-testid={`edit-coach-${coach.coach_id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(coach.team_id, coach.coach_id)}
                        className="text-muted-foreground hover:text-red-400"
                        data-testid={`remove-coach-${coach.coach_id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Coach Permissions Info */}
      <div className="bg-card border border-border/40 rounded-sm p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Shield className="text-primary" size={18} />
          Coach Permissions
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-emerald-400 font-medium mb-1">Coaches CAN:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Access team workspace</li>
              <li>• Manage training sessions</li>
              <li>• Upload drills and plans</li>
              <li>• Analyze videos</li>
            </ul>
          </div>
          <div>
            <p className="text-red-400 font-medium mb-1">Coaches CANNOT:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Access financial data</li>
              <li>• Control player billing</li>
              <li>• Change pass ownership</li>
              <li>• Invite players outside club</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-6">Invite Coach</h2>
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Coach Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    required
                    className="bg-transparent border-border pl-10 focus:border-primary"
                    placeholder="coach@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Assign to Team *
                </label>
                <select
                  value={inviteForm.team_id}
                  onChange={(e) => setInviteForm({...inviteForm, team_id: e.target.value})}
                  required
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-primary"
                >
                  <option value="" className="bg-card">Select a team</option>
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Role
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-primary"
                >
                  <option value="head_coach" className="bg-card">Head Coach</option>
                  <option value="assistant" className="bg-card">Assistant Coach</option>
                </select>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button 
                  type="submit"
                  className="w-full bg-primary text-black hover:bg-primary/90"
                >
                  <Mail size={16} className="mr-2" />
                  Send Email Invite
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleGenerateLinkOnly}
                  className="w-full border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Link2 size={16} className="mr-2" />
                  Generate Invite Link Only
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowInviteModal(false)}
                  className="w-full text-muted-foreground"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Invite Link Modal */}
      {showInviteLinkModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowInviteLinkModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase">Invite Link Generated</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Share this link with the coach to join your team
              </p>
            </div>
            
            <div className="bg-card/50 border border-border/40 rounded-sm p-4 mb-4">
              <p className="text-xs text-muted-foreground uppercase mb-2">Invite Link</p>
              <div className="flex gap-2">
                <Input
                  value={generatedInviteLink}
                  readOnly
                  className="bg-transparent border-border text-xs flex-1"
                />
                <Button
                  onClick={() => copyToClipboard(generatedInviteLink)}
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 rounded-sm p-3 mb-4">
              <p className="text-xs text-primary">
                The coach will be able to access the team workspace after clicking this link and creating their account.
              </p>
            </div>

            <Button 
              onClick={() => setShowInviteLinkModal(false)}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Done
            </Button>
          </motion.div>
        </div>
      )}

      {/* Edit Coach Modal */}
      {showEditModal && selectedCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-coach-modal"
          >
            <h2 className="font-heading text-xl uppercase mb-6">Edit Coach Assignment</h2>
            
            <div className="mb-4 p-3 bg-card/50 border border-border/40 rounded-sm">
              <p className="text-sm font-medium">{selectedCoach.coach_name}</p>
              <p className="text-xs text-muted-foreground">{selectedCoach.coach_email}</p>
            </div>
            
            <form onSubmit={handleUpdateCoach} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Assigned Team
                </label>
                <select
                  value={editForm.team_id}
                  onChange={(e) => setEditForm({...editForm, team_id: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-primary"
                  data-testid="edit-coach-team-select"
                >
                  {teams.filter(t => t.status === 'active').map(team => (
                    <option key={team.id} value={team.id} className="bg-card">{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Role
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-primary"
                  data-testid="edit-coach-role-select"
                >
                  <option value="head_coach" className="bg-card">Head Coach</option>
                  <option value="assistant" className="bg-card">Assistant Coach</option>
                </select>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  className="w-full bg-transparent border border-border rounded-sm p-3 text-white focus:border-primary"
                  data-testid="edit-coach-status-select"
                >
                  <option value="active" className="bg-card">Active</option>
                  <option value="pending" className="bg-card">Pending</option>
                  <option value="inactive" className="bg-card">Inactive</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 text-muted-foreground"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-primary text-black hover:bg-primary/90"
                  data-testid="save-coach-changes-btn"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClubCoaches;
